import { mergeAgainstCommonBase } from '@cf-shared/deep-helpers';
import { FitnessState } from '@cf-shared/data-model/user-state';

/**
 * df-5556257f1229: which fitness state the intake flow's Finish button stores when the user
 * edited their workouts from the program summary.
 *
 * Neither copy the finish is holding may simply win. The stored document, filled from the
 * intake answers, never saw the Edit Workouts session. The copy Edit Workouts returned was
 * taken from the database when that page opened, so on a new user's run it never held the
 * declared strength the answers project into `exerciseStats`. So this asks a third question —
 * what did the Edit Workouts VISIT change? — against the copy the page took before that
 * visit, and merges only that.
 *
 * The whole story is in `design-docs/sync-integrity.md`, "The same finish, for the FITNESS
 * STATE: merged, not chosen (df-5556257f1229)": what was being lost, why a merge rather than
 * a choice, and why the starting point is taken once rather than per visit.
 */
export interface FinishFitnessStateChoice {
  /** the fitness state content to store */
  state: FitnessState;
  /**
   * how many fields the merge could not settle from the shared starting point and had to
   * guess at — a condition to report. Two shapes reach it, and only the first loses anything:
   * the intake answers and the Edit Workouts visit changed the same field to DIFFERENT values,
   * so one of them was dropped; or one side deleted a field the other changed, where the merge
   * keeps the surviving value and nothing is lost. `mergeAgainstCommonBase` counts both.
   */
  guessedFieldCount: number;
  /**
   * true when the merge ran without a usable shared starting point and fell back to its
   * two-way rules. Should be impossible here: the caller only merges when it HAS the
   * before-edit copy, so this means that copy was not a document shape the merge can walk.
   */
  mergedWithoutBase: boolean;
}

/**
 * Decide what the intake finish stores in the fitness state.
 *
 * @param beforeEditVisit the page's copy of the fitness state from before its FIRST Edit
 *   Workouts visit, or undefined when the user never opened that page — the shared starting
 *   point the two documents below are compared against
 * @param filledFromIntakeAnswers the stored document, already filled from the intake answers.
 *   This is what the finish stored before this existed, and it is still what gets stored
 *   whenever there is no Edit Workouts visit to merge
 * @param returnedFromEditWorkouts the fitness state Edit Workouts handed back, or undefined
 *   when it handed none back
 */
export function chooseFitnessStateToPersistAtFinish(
  beforeEditVisit: FitnessState | undefined,
  filledFromIntakeAnswers: FitnessState,
  returnedFromEditWorkouts: FitnessState | undefined,
): FinishFitnessStateChoice {
  // if the user never opened Edit Workouts, or it handed nothing back, there is no edit to
  // carry and the finish stores exactly what it has always stored
  if (!beforeEditVisit || !returnedFromEditWorkouts) {
    return { state: filledFromIntakeAnswers, guessedFieldCount: 0, mergedWithoutBase: false };
  }

  // if the page is still holding the very object the database holds, no Edit Workouts visit
  // has replaced it, so there is likewise nothing to merge
  if (returnedFromEditWorkouts === filledFromIntakeAnswers) {
    return { state: filledFromIntakeAnswers, guessedFieldCount: 0, mergedWithoutBase: false };
  }

  const result = mergeAgainstCommonBase<FitnessState>(
    beforeEditVisit, filledFromIntakeAnswers, returnedFromEditWorkouts);
  return {
    state: result.merged,
    guessedFieldCount: result.guessedFieldCount,
    mergedWithoutBase: result.mergedWithoutBase,
  };
}
